// src/features/dashboard/components/Dashboard.jsx
import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Paper,
} from "@mui/material";
import {
  PhotoCamera,
  Receipt,
  Description,
  People,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";

const Dashboard = () => {
  const { stats: invoiceStats } = useSelector(
    (state) => state.invoices || { stats: {} },
  );
  const { stats: galleryStats } = useSelector(
    (state) => state.gallery || { stats: {} },
  );

  const stats = {
    totalPhotos: galleryStats?.total || 0,
    totalInvoices: invoiceStats?.total || 0,
    totalQuotations: 0,
    totalClients: 0,
    revenue: 0,
    growth: 12.5,
  };

  const statCards = [
    {
      title: "Total Photos",
      value: stats.totalPhotos,
      icon: <PhotoCamera sx={{ fontSize: 40 }} />,
      color: "#667eea",
      trend: "+12%",
    },
    {
      title: "Total Invoices",
      value: stats.totalInvoices,
      icon: <Receipt sx={{ fontSize: 40 }} />,
      color: "#764ba2",
      trend: "+8%",
    },
    {
      title: "Total Quotations",
      value: stats.totalQuotations,
      icon: <Description sx={{ fontSize: 40 }} />,
      color: "#52c41a",
      trend: "+15%",
    },
    {
      title: "Total Clients",
      value: stats.totalClients,
      icon: <People sx={{ fontSize: 40 }} />,
      color: "#faad14",
      trend: "+5%",
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Welcome back! Here's what's happening with your photography studio.
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: "100%",
                background: `linear-gradient(135deg, ${stat.color}22 0%, ${stat.color}11 100%)`,
                borderLeft: `4px solid ${stat.color}`,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      gutterBottom
                    >
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{ mt: 1 }}
                    >
                      {stat.trend} from last month
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: `${stat.color}22`,
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Recent Activity
            </Typography>
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading recent activity...
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                • Add new photos to gallery
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Create new invoice
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Send quotations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Manage clients
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
